/**
 * Internaționalizare (RO/EN) — același tipar ca InOut și Investigații:
 * traducerea se caută după TEXTUL ROMÂNESC SURSĂ, nu după chei artificiale.
 * Consecința practică e că un șir netradus rămâne pur și simplu în română —
 * niciodată o cheie brută pe ecran, niciodată un gol.
 *
 * O singură abatere de la tiparul celorlalte două aplicații, cu motiv:
 * acolo `lang` e ridicat în App și coborât ca prop prin fiecare ecran. Aici
 * sunt 31 de ecrane cu componente imbricate pe mai multe niveluri, iar
 * coborârea unui prop prin toate ar fi fost o schimbare mult mai mare decât
 * traducerea însăși — și exact genul de schimbare care uită un nivel. Deci
 * limba curentă stă în modulul ăsta, iar `tr()` o citește. Se numește `tr`,
 * nu `t`: în ecranele astea `t` e deja numele a 36 de variabile locale
 * (`([v, t]) => <option>{t}</option>`), iar o funcție umbrită de un șir nu dă
 * nicio eroare la build — dă un „t is not a function" abia pe ecran.
 *
 * Reîmprospătarea o dă App: ține limba și ca STARE, nu doar în modulul ăsta,
 * deci schimbarea ei rerandează tot arborele. Verificat, nu presupus, că e
 * de-ajuns: niciun `useMemo` din aplicație nu produce text tradus, deci n-are
 * ce rămâne în urmă.
 *
 * Ce se traduce: chrome-ul aplicației, mesajele ei, vocabularul pe care îl
 * LIVRĂM noi (nomenclator, operații, catalog, șabloane). Ce NU se traduce, ca
 * peste tot pe platformă: datele scrise de firmă — denumiri de clienți,
 * observații scrise liber, motive. Nu avem cum să traducem textul altcuiva.
 */
import { useState } from "react";


const CHEIE = "mmt-limba";

function citeste() {
  try {
    return localStorage.getItem(CHEIE) === "en" ? "en" : "ro";
  } catch {
    return "ro";
  }
}

let curenta = citeste();

/**
 * Dicționarul se încarcă la CERERE, nu odată cu aplicația: are 185 KB, iar un
 * utilizator care lucrează în română nu are de ce să-i plătească transferul.
 * Până se încarcă (sau dacă încărcarea eșuează), `tr()` întoarce româna — adică
 * exact comportamentul de rezervă pe care se sprijină tot mecanismul.
 */
let EN = {};
let EN_TIPARE = [];
let incarcat = false;

export async function pregatesteLimba() {
  if (curenta !== "en" || incarcat) return;
  try {
    const m = await import("./i18n-en.js");
    EN = m.EN;
    EN_TIPARE = m.EN_TIPARE;
    tipareCompilate = null;
    incarcat = true;
  } catch {
    // rămâne româna: un dicționar care nu s-a încărcat nu golește ecranul
  }
}

export function limba() {
  return curenta;
}

export function seteazaLimba(l) {
  curenta = l === "en" ? "en" : "ro";
  try {
    localStorage.setItem(CHEIE, curenta);
  } catch {
    // fără localStorage limba rămâne validă doar pentru sesiunea curentă
  }
}

export function useLimba() {
  const [l, setL] = useState(curenta);
  return [l, async (nou) => {
    seteazaLimba(nou);
    await pregatesteLimba();
    setL(limba());
  }];
}

/**
 * `tr("Au rămas {0} zile", n)` — nu `tr("Au rămas ") + n + tr(" zile")`.
 *
 * Șabloanele cu locuri numerotate există tocmai ca traducerea să poată schimba
 * ORDINEA: în engleză bucata care în română vine prima poate veni ultima, iar
 * două traduceri lipite cap la cap n-ar avea cum s-o facă.
 */
export function tr(text, ...valori) {
  // `tr(x?.denumire)` e un tipar des întâlnit în ecrane, iar `x` poate lipsi.
  // Fără garda asta, o cheie inexistentă întoarce `undefined` — azi cade
  // cuminte pe „—", dar primul apel cu argumente de formatare ar da un
  // `.replace` pe `undefined`. Costă o linie.
  if (typeof text !== "string") return text;
  const s = curenta === "en" ? (EN[text] ?? text) : text;
  if (!valori.length) return s;
  return s.replace(/\{(\d+)\}/g, (gasit, i) => {
    const v = valori[Number(i)];
    return v === undefined || v === null ? "" : String(v);
  });
}

/** Formatul locului pentru date și numere. Nu e o preferință: 15.01.2029 nu se citește la fel peste tot. */
export function locale() {
  return curenta === "en" ? "en-GB" : "ro-RO";
}

/**
 * Mesajele venite de la server.
 *
 * Se traduc AICI, pe front, nu în backend: nu există un canal de limbă între
 * cele două (nici Accept-Language, nici cont cu limbă), iar o instalare
 * on-premise cu un server mai vechi ar rămâne altfel fără traduceri. Cheia e
 * chiar mesajul românesc, ca peste tot.
 *
 * Cele care poartă valori („Rândul 7 cere o valoare măsurată") nu se pot căuta
 * exact — ajung aici cu valorile deja puse — deci se potrivesc ca TIPAR, cu
 * șablonul românesc transformat în expresie regulată.
 */
let tipareCompilate = null;
function tipare() {
  if (!tipareCompilate) {
    tipareCompilate = EN_TIPARE.map(([ro, en]) => {
      const bucati = ro.split(/\{\d+\}/g);
      const escapa = (x) => x.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      return [new RegExp("^" + bucati.map(escapa).join("(.+?)") + "$", "s"), en];
    });
  }
  return tipareCompilate;
}

export function trMesajServer(text) {
  if (curenta !== "en" || typeof text !== "string") return text;
  const exact = EN[text];
  if (exact) return exact;
  for (const [re, en] of tipare()) {
    const m = text.match(re);
    if (m) return en.replace(/\{(\d+)\}/g, (gasit, i) => m[Number(i) + 1] ?? "");
  }
  return text;
}
